import { movieService, type MovieListItem } from '@/services/movieService';
import type { PlaybackLanguageParams } from '@/services/tvShowService';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { TableAction, TableColumn, MovieGenre } from '@/types';
import { ArrowUpToLine, Edit, LoaderCircle, Play, Plus, Search, Trash2 } from 'lucide-react';
import { useMovie, useMovieList, useDeleteMovie, useToggleMovieStatus, usePromoteMovie } from '@/hooks/useMovies';
import DataTableWithQuery from '@/components/ui/DataTableWithQuery';
import Toggle from '@/components/ui/Toggle';
import { useCategoriesForDropdown, useConfirmDialog, useTranslation } from '@/hooks';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import AutocompleteSelect from '@/components/ui/AutocompleteSelect';
import Modal from '@/components/ui/Modal';
import VodPlayer from '@/components/media/VodPlayer';
import DropdownActions from '@/components/ui/DropdownActions';

const Movies = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const deleteMovieMutation = useDeleteMovie();
  const toggleStatusMutation = useToggleMovieStatus();
  const promoteMovieMutation = usePromoteMovie();
  const { t } = useTranslation();
  const { confirm, dialog } = useConfirmDialog();
  const [playingMovie, setPlayingMovie] = useState<MovieListItem | null>(null);
  const { data: playbackMovie, isLoading: playbackMovieLoading, isError: playbackMovieError } = useMovie(playingMovie?.id ?? '');
  const categoryFilter = searchParams.get('categories');
  const { data: categories } = useCategoriesForDropdown('MOVIE');

  const loadPlayingMovieUrl = useCallback((params: PlaybackLanguageParams) => {
    if (!playingMovie) {
      return Promise.reject(new Error('No movie selected'));
    }
    return movieService.getMoviePlaybackUrl(playingMovie.id, params);
  }, [playingMovie]);

  const categoryOptions = useMemo(
    () =>
      (categories ?? []).map((category) => ({
        value: category.id,
        label: category.description || category.englishDescription,
      })),
    [categories]
  );

  const initialFilters = useMemo(
    () => ({
      categories: categoryFilter || undefined,
    }),
    [categoryFilter]
  );

  const updateCategoryFilterParam = (value: string) => {
    const nextParams = new URLSearchParams(searchParams);

    if (value) {
      nextParams.set('categories', value);
    } else {
      nextParams.delete('categories');
    }

    setSearchParams(nextParams);
  };

  const movieColumns: TableColumn<MovieListItem>[] = [
    {
      sortable: true,
      key: 'title',
      header: 'Título',
      accessor: (movie) => (
        <div className="font-medium text-gray-900">
          <Link to={`/content/movies/${movie.id}/edit`} className="link">
            {movie.title}
          </Link>
        </div>
      ),
      className: 'min-w-48'
    },
    {
      sortable: true,
      key: 'year',
      header: 'Año',
      accessor: (movie) => (
        <div className="text-gray-700">
          {movie.year}
        </div>
      )
    },
    {
      key: 'genres',
      header: 'Géneros',
      accessor: (movie) => (
        <div className="text-gray-700">
          <MovieGenresList genres={movie.genres}/>
        </div>
      ),
      className: 'min-w-32'
    },
    {
      key: "categoryNames",
      align: 'center',
      header: t.tvChannels.fields.categories,
      accessor: (movie) => (
        <div className="flex flex-wrap gap-1 justify-center">
          {movie.categoryNames.map((category, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-400 text-white"
            >
              {category}
            </span>
          ))}
        </div>
      )
    },
    {
      sortable: true,
      key: 'views',
      header: 'Visitas',
      accessor: (movie) => (
        <div className="text-gray-700">
          {movie.views?.toLocaleString() || 0}
        </div>
      )
    },
    {
      sortable: true,
      key: 'active',
      header: 'Activo',
      accessor: (movie) => (
        <Toggle
          checked={movie.active}
          onChange={(checked) => handleToggleStatus(movie.id, checked)}
          disabled={toggleStatusMutation.isPending}
          size="sm"
        />
      )
    },
    {
      key: 'updatedDate',
      sortable: true,
      header: t.common.fields.updatedDate,
      accessor: (sport) => (
        <div className="text-gray-500">
          {sport.updatedDate ? new Date(sport.updatedDate).toLocaleDateString('es'):'N/D'}
        </div>
      ),
    },
    {
      key: 'actions',
      header: t.links.actions,
      align: 'right',
      className: 'w-px',
      accessor: (movie) => (
        <DropdownActions
          item={movie}
          actions={movieActions}
          triggerLabel={`${t.links.actions}: ${movie.title}`}
          primaryAction={{
            label: movie.active ? `${t.vodPlayer.playMovie}: ${movie.title}` : t.vodPlayer.noMedia,
            icon: Play,
            onClick: setPlayingMovie,
            disabled: !movie.active,
            className: 'text-indigo-600 hover:border-indigo-300 hover:text-indigo-800',
          }}
        />
      ),
    },
  ];

  const movieActions: TableAction<MovieListItem>[] = [
    {
      label: 'Mover al frente',
      icon: ArrowUpToLine,
      onClick: (movie) => handlePromoteMovie(movie.id),
      className: 'text-amber-600 hover:text-amber-800'
    },
    {
      label: 'Editar',
      icon: Edit,
      onClick: (movie) => navigate(`/content/movies/${movie.id}/edit`),
      className: 'text-gray-700 hover:text-indigo-600'
    },
    {
      label: 'Eliminar',
      icon: Trash2,
      onClick: (movie) => handleDeleteMovie(movie.id),
      className: 'text-red-600 hover:text-red-800'
    }
  ];

  const handlePromoteMovie = async (id: string) => {
    try {
      await promoteMovieMutation.mutateAsync(id);
      toast.success('Película movida al frente exitosamente');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const handleCreateMovie = () => {
    navigate('/content/movies/create');
  };

  const handleDeleteMovie = async (id: string) => {
    try {
      const confirmed = await confirm({
        title: t.common.confirmationTitle,
        message: '¿Está seguro de que desea eliminar esta película?',
        confirmLabel: t.buttons.delete,
        cancelLabel: t.buttons.cancel,
        confirmVariant: 'danger',
        onConfirm: async () => {
          await deleteMovieMutation.mutateAsync(id);
        },
      });

      if (!confirmed) {
        return;
      }
      console.log('Película eliminada exitosamente');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
      return;
    }

    toast.success('Película eliminada exitosamente');
  };

  const handleToggleStatus = async (id: string, active: boolean) => {
    try {
      await toggleStatusMutation.mutateAsync({ id, active });
      console.log('Estado actualizado exitosamente');
    } catch (error) {
      console.error('Toggle status error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <DataTableWithQuery
          showTotalCount
          tableStyle="compact"
          columns={movieColumns}
          useQueryHook={useMovieList}
          headerRenderer={({ searchQuery, onSearchChange, searchPlaceholder, totalCount }) => (
            <div className="border-b border-gray-200 bg-white">
              <div className="px-4 py-2 md:hidden">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-gray-900">Películas</h3>
                    <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                      {totalCount}
                    </span>
                  </div>
                  <Button onClick={handleCreateMovie} size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Nueva película
                  </Button>
                </div>
                <div className="relative mt-3">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div className="mt-3">
                  <AutocompleteSelect
                    value={categoryFilter ?? ''}
                    onChange={updateCategoryFilterParam}
                    options={categoryOptions}
                    placeholder="Todas las categorías"
                    size="sm"
                    className="text-base"
                    allowClear
                  />
                </div>
              </div>
              <div className="hidden flex-wrap items-center gap-3 px-4 py-2 md:flex">
                <h3 className="text-lg font-medium text-gray-900">Películas</h3>
                <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                  {totalCount}
                </span>
                <div className="relative flex-1 min-w-[220px]">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div className="min-w-[210px]">
                  <AutocompleteSelect
                    value={categoryFilter ?? ''}
                    onChange={updateCategoryFilterParam}
                    options={categoryOptions}
                    placeholder="Todas las categorías"
                    size="sm"
                    className="text-base"
                    allowClear
                  />
                </div>
                <div className="ml-auto">
                  <Button onClick={handleCreateMovie} size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Crear nueva película
                  </Button>
                </div>
              </div>
            </div>
          )}
          onCreate={handleCreateMovie}
          searchPlaceholder="Buscar películas..."
          title="Películas"
          itemsPerPage={20}
          initialFilters={initialFilters}
          className="rounded-none shadow-none border border-gray-200"
        />
        <Modal
          isOpen={Boolean(playingMovie)}
          onClose={() => setPlayingMovie(null)}
          title={playingMovie
            ? `${t.vodPlayer.movieTitle}: ${playingMovie.title}`
            : t.vodPlayer.movieTitle}
          size="2xl"
        >
          {playbackMovieLoading ? (
            <div role="status" className="flex aspect-video flex-col items-center justify-center gap-2">
              <LoaderCircle className="h-8 w-8 animate-spin" />
              <p className="text-sm">{t.vodPlayer.loading}</p>
            </div>
          ) : playbackMovieError ? (
            <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {t.vodPlayer.playbackError}
            </p>
          ) : playingMovie && playbackMovie && (
            <VodPlayer
              key={playingMovie.id}
              title={playingMovie.title}
              links={playbackMovie.links}
              poster={playbackMovie.coverImageUrl}
              loadPlaybackUrl={loadPlayingMovieUrl}
            />
          )}
        </Modal>
        {dialog}
      </div>
    </div>
  );
};

const MovieGenresList = ({ genres }: { genres: MovieGenre[] }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-1">
      {genres.map((genre) => (
        <span
          key={genre}
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-400 text-white"
        >
          {t.movies.genres[genre as keyof typeof t.movies.genres] || genre}
        </span>
      ))}
    </div>
  );
}

export default Movies;
